// ============================================================================
// Primitives locales de l'onglet Gestion (L09) — tokens DS, dark-aware
// ============================================================================
// packages/ui est INTERDIT a ce frag et le parc gest n'a ni WizardDialog ni
// DetailDialog ni Sparkline (primitives du PoC). On rebatit le STRICT minimum sur
// les tokens (@gs/ui GS_COMPONENTS + echelle typographique + couleurs slate/brand),
// comme le font deja NewReminderDialog (modal) et FinanceYearChart (SVG maison).
// Regle #28 : zero CSS en dur cote couleur, zero rustine dark — chaque surface
// porte sa variante `dark:`. Regle #4 : zero emoji dans ce fichier.

import { useEffect, useRef, type ReactNode } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { X, ArrowUpRight } from 'lucide-react';

// ─── Carte de base ────────────────────────────────────────────────────────────

export function Card({
  children,
  className = '',
  emphasis = false,
}: {
  children: ReactNode;
  className?: string;
  /** M-756 : la carte Taux reel s'emphase quand un hors-grille n'a pas de taux. */
  emphasis?: boolean;
}) {
  const ring = emphasis ? 'ring-1 ring-brand-300 dark:ring-brand-500/50' : '';
  return <div className={`${GS_COMPONENTS.card} p-4 ${ring} ${className}`}>{children}</div>;
}

export function CardHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="mb-3 flex flex-row flex-wrap items-center justify-between gap-2">
      <h3 className="text-field font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
      {action}
    </div>
  );
}

// ─── Badge ──────────────────────────────────────────────────────────────────

export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand';

const BADGE_TONE: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  info: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  danger: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300',
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-950/50 dark:text-brand-300',
};

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-chip font-medium ${BADGE_TONE[tone]}`}>
      {children}
    </span>
  );
}

// ─── StatCard (tuile) ─────────────────────────────────────────────────────────

export function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'brand' | 'success';
}) {
  const valueTone =
    tone === 'brand'
      ? 'text-brand-600 dark:text-brand-400'
      : tone === 'success'
        ? 'text-emerald-700 dark:text-emerald-400'
        : 'text-slate-800 dark:text-slate-100';
  return (
    <div className={`${GS_COMPONENTS.card} p-3`}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-head font-bold tabular-nums ${valueTone}`}>{value}</p>
      {hint ? <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">{hint}</p> : null}
    </div>
  );
}

// ─── DataRow (ligne titre/sous-titre + trailing) ─────────────────────────────

export function DataRow({
  leading,
  title,
  subtitle,
  trailing,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700">
      <div className="flex min-w-0 items-center gap-2">
        {leading}
        <div className="min-w-0">
          <p className="truncate text-field text-slate-800 dark:text-slate-100">{title}</p>
          {subtitle ? <p className="truncate text-meta text-slate-500 dark:text-slate-400">{subtitle}</p> : null}
        </div>
      </div>
      {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
    </div>
  );
}

// ─── Modal (shell partage — gabarit NewReminderDialog) ───────────────────────

export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const width = size === 'xl' ? 'max-w-3xl' : size === 'lg' ? 'max-w-2xl' : 'max-w-lg';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={ref} className={`flex max-h-full w-full ${width} flex-col gap-4 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-slate-800`}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-body font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={title}
            className="touch-manipulation rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Pied de modal standard : Annuler + action principale (disabled si pending). */
export function ModalFooter({
  onCancel,
  cancelLabel,
  submitLabel,
  onSubmit,
  pending,
  secondaryLabel,
  onSecondary,
}: {
  onCancel: () => void;
  cancelLabel: string;
  submitLabel: string;
  onSubmit: () => void;
  pending?: boolean;
  secondaryLabel?: string;
  onSecondary?: () => void;
}) {
  return (
    <div className="mt-1 flex items-center justify-end gap-2">
      <button type="button" onClick={onCancel} className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}>
        {cancelLabel}
      </button>
      {secondaryLabel && onSecondary ? (
        <button type="button" onClick={onSecondary} disabled={pending} className={`${GS_COMPONENTS.btnGhost} touch-manipulation`}>
          {secondaryLabel}
        </button>
      ) : null}
      <button type="button" onClick={onSubmit} disabled={pending} className={GS_COMPONENTS.btnAction}>
        {submitLabel}
      </button>
    </div>
  );
}

// ─── DrillCard (tuile KPI cliquable -> Modal de detail) ──────────────────────
// PoC: DrillCard = bouton stylise card + DetailDialog(children). Ici: bouton + Modal.

export function DrillCard({
  eyebrow,
  value,
  hint,
  badge,
  title,
  size = 'lg',
  open,
  onToggle,
  children,
}: {
  eyebrow: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  badge?: ReactNode;
  title: string;
  size?: 'lg' | 'xl';
  open: boolean;
  onToggle: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <>
      <button
        type="button"
        onClick={() => onToggle(true)}
        className={`${GS_COMPONENTS.card} flex touch-manipulation flex-col gap-1.5 p-3 text-left transition-colors hover:border-brand-300 dark:hover:border-brand-500/60`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-chip font-medium uppercase tracking-wide text-slate-400">{eyebrow}</span>
          <ArrowUpRight size={16} aria-hidden="true" className="shrink-0 text-slate-400" />
        </div>
        <span className="text-body font-semibold tabular-nums text-slate-800 dark:text-slate-100">{value}</span>
        <div className="flex items-center justify-between gap-2">
          {hint ? <span className="text-meta text-slate-500 dark:text-slate-400">{hint}</span> : <span />}
          {badge}
        </div>
      </button>
      <Modal open={open} onClose={() => onToggle(false)} title={title} size={size}>
        {children}
      </Modal>
    </>
  );
}

// ─── EmptyRow (etat vide compact dans une carte) ─────────────────────────────

export function EmptyRow({ title, description }: { title: string; description?: string }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center dark:border-slate-700">
      <p className="text-field font-medium text-slate-600 dark:text-slate-300">{title}</p>
      {description ? <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">{description}</p> : null}
    </div>
  );
}

// ─── Sparkline (SVG maison — enveloppe FinanceYearChart) ─────────────────────

export function Sparkline({
  data,
  width = 90,
  height = 26,
  reference,
  className = 'text-brand-500',
}: {
  data: number[];
  width?: number;
  height?: number;
  reference?: number;
  className?: string;
}) {
  if (data.length < 2) return null;
  const min = Math.min(...data, reference ?? Infinity);
  const max = Math.max(...data, reference ?? -Infinity);
  const span = max - min || 1;
  const x = (i: number) => (i / (data.length - 1)) * width;
  const y = (v: number) => height - ((v - min) / span) * height;
  const pts = data.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={className} aria-hidden="true">
      {reference != null ? (
        <line x1={0} x2={width} y1={y(reference)} y2={y(reference)} stroke="currentColor" strokeWidth={1} opacity={0.3} strokeDasharray="2 2" />
      ) : null}
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Mini barres verticales (pouls — MiniBarChart du PoC). */
export function MiniBars({
  data,
  max,
  width = 90,
  height = 28,
  className = 'text-brand-500',
}: {
  data: number[];
  max: number;
  width?: number;
  height?: number;
  className?: string;
}) {
  if (data.length === 0) return null;
  const bw = width / data.length;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={className} aria-hidden="true">
      {data.map((v, i) => {
        const h = Math.max(1, (v / max) * height);
        return <rect key={i} x={i * bw + 1} y={height - h} width={Math.max(1, bw - 2)} height={h} rx={1} fill="currentColor" opacity={0.85} />;
      })}
    </svg>
  );
}

/** Jauge horizontale (GaugeBar du PoC). */
export function GaugeBar({ value, max, className = 'bg-emerald-500' }: { value: number; max: number; className?: string }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
      <div className={`h-full ${className}`} style={{ width: `${pct}%` }} />
    </div>
  );
}
